import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import GrowthComparison from "../../GrowthComparison"
import { neumorph } from "../neumorph"
import { formatCurrency } from "../KpiHeader"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const GrowthCard = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [rawData, setRawData] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/growth-comparison`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setRawData(res.data.data || [])
        else setRawData([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("GrowthCard fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer])

  // Pivot to year totals + best chart prep
  const years = [...new Set(rawData.map((r) => r.year))].sort()
  const yearTotals = years.map((y) => ({
    year: y,
    total: rawData
      .filter((r) => r.year === y)
      .reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0),
  }))

  const colors = [
    "rgba(156, 163, 175, 0.7)",
    "rgba(96, 165, 250, 0.7)",
    "rgba(88, 192, 135, 0.8)",
  ]

  const chartData = years.length
    ? {
        labels: MONTHS,
        datasets: years.map((y, idx) => ({
          label: String(y),
          data: MONTHS.map((_, mi) => {
            const row = rawData.find((r) => r.year === y && r.month_num === mi + 1)
            return row ? parseFloat(row.total_amount) : 0
          }),
          backgroundColor: colors[idx] || "rgba(100,100,100,0.5)",
          borderRadius: 3,
        })),
      }
    : null

  const fmtCompactRand = (v) => {
    if (v >= 1_000_000) return `R${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `R${(v / 1_000).toFixed(0)}k`
    return `R${v}`
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: { display: true, grid: { display: false }, ticks: { font: { size: 8 }, color: neumorph.textMuted } },
      y: {
        display: true,
        beginAtZero: true,
        grid: { color: "rgba(174,184,200,0.15)", drawBorder: false },
        ticks: { font: { size: 9 }, color: neumorph.textMuted, maxTicksLimit: 4, callback: fmtCompactRand },
      },
    },
  }

  // Compute YoY change for the latest two years
  let yoyChange = null
  if (yearTotals.length >= 2) {
    const last = yearTotals[yearTotals.length - 1].total
    const prev = yearTotals[yearTotals.length - 2].total
    if (prev > 0) yoyChange = ((last - prev) / prev) * 100
  }

  return (
    <>
      <BentoCard
        title="3-Year Growth"
        subtitle="Monthly revenue across years"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        <Flex direction="column" h="100%" justify="space-between">
          <Flex gap={3} mb={2} wrap="wrap">
            {yearTotals.slice(-2).map((yt, i) => (
              <Box key={yt.year}>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  {yt.year}
                </Text>
                <Text fontSize="sm" fontWeight="700" color={neumorph.textPrimary}>
                  {formatCurrency(yt.total)}
                </Text>
              </Box>
            ))}
            {yoyChange !== null && (
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  YoY
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color={yoyChange >= 0 ? neumorph.accentGreen : neumorph.accentRed}
                >
                  {yoyChange >= 0 ? "+" : ""}
                  {yoyChange.toFixed(1)}%
                </Text>
              </Box>
            )}
          </Flex>
          {chartData ? (
            <Box h="120px">
              <Bar data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Text fontSize="xs" color={neumorph.textMuted}>
              No data
            </Text>
          )}
        </Flex>
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="3-Year Growth Comparison">
        <GrowthComparison supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default GrowthCard
